import { Button } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import type { ColDef, GridApi } from 'ag-grid-community';
import copy from 'clipboard-copy';
import React, { useContext, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { notify } from 'src/clients/ApiService';
import AgGrid, { AgGridContext } from 'src/components/AgGrid';
import { TextCellRenderer } from 'src/components/AgGrid/TextCellRenderer';
import {
  CopyIcon,
  Download,
  SignatureCheckboxIcon,
  SignatureDateIcon,
  SignatureIcon,
  SignatureInitialIcon,
  SignatureTextIcon,
  TrashIcon,
} from 'src/components/icons';
import { StatusCellRenderer } from 'src/components/StatusCellRenderer';
import { CONTRACTS_PAGE, User } from 'src/constants';
import { PortalConfigContext, RouteContext } from 'src/context';
import { Contract } from 'src/entities/Contract';
import { CreateClientMagicLinkDialog } from 'src/features/magic-links/CreateClientMagicLinkDialog';
import history from 'src/history';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { useModuleIcon } from 'src/hooks/useModuleIcon';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { getFileNameFromFileKey } from 'src/legacy/components/Files/helpers';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import {
  ContractStatus,
  ContractTemplate,
  FieldType,
  InputType,
  useCancelContractMutation,
  useGetContractsQuery,
} from 'src/services/api';
import { RootState } from 'src/store';
import { DateUtils, FileUtils, S3Utils, UrlUtils } from 'src/utils';
import { arrayToMap } from 'src/utils/array';
import { ensureUnreachable } from 'src/utils/common_utils';

import { Action, BaseActionsMenu } from '../Dropdowns';

import { getContractStatusInfo } from './ClientContractsTable';
import { emitContractDownloadedAuditEvent } from './contract_helpers';
import { ContractClientRenderer } from './ContractClientRenderer';
import { ContractDetailsPageQueryParams } from './ContractDetailsPage';
import { ShareContractModal } from './ShareContractModal';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(0, 4),
    marginTop: theme.spacing(3),
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      marginTop: 0,
    },
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& svg': {
      height: 16,
      width: 16,
    },
  },
}));

/**
 * Determines the appropriate signature icon based on the contract field type
 */
const getSignIconBasedOnFieldType = (type: FieldType) => {
  switch (type) {
    case FieldType.Date:
      return <SignatureDateIcon />;

    case FieldType.Initials:
      return <SignatureInitialIcon />;
    case FieldType.Signature:
      return <SignatureIcon />;
    case FieldType.Text:
      return <SignatureTextIcon />;
    case FieldType.Checkbox:
      return <SignatureCheckboxIcon />;
    default:
      return ensureUnreachable(type);
  }
};

/**
 * Component responsible for rendering actions for submission and requests table rows.
 */
export const SubmissionTableActionMenu = ({
  data,
  clientsMap,
  enableMagicLinks = false,
}: {
  data: Contract;
  clientsMap: Map<string, User>;
  enableMagicLinks?: boolean;
}) => {
  const [cancelContract] = useCancelContractMutation();
  const { basePortalDomain } = useCustomDomains();
  const [userToInvite, setUserToInvite] = React.useState<User>();
  const dispatch = useAppDispatch();
  const handleDownload = async () => {
    const { signedFileUrl, fileUrl } = data;

    const stagedFileKey =
      data.status === ContractStatus.Signed ? signedFileUrl : fileUrl;

    const s3Url = await S3Utils.getFile(stagedFileKey, {
      identityId: data.identityId,
      level: 'protected',
    });

    await FileUtils.downloadFileFromUrl(
      s3Url,
      stagedFileKey,
      getFileNameFromFileKey(stagedFileKey),
    );

    emitContractDownloadedAuditEvent(data);
  };

  const handleCopyShareLink = async () => {
    const shareContractLink = UrlUtils.GetFullUrl(
      `${basePortalDomain}/sl/contract/${data.ref}`,
    );
    try {
      await copy(shareContractLink);
      notify({
        status: 'success',
        successMessage: 'eSign link copied to clipboard',
        dispatch,
      });
    } catch (e) {
      notify({
        status: 'error',
        errorMessage: 'Failed to copy eSign link',
        dispatch,
      });
    }
  };

  const copyContractAction = enableMagicLinks
    ? {
        name: 'Copy eSign link',
        icon: <CopyIcon />,
        onClick: () => {
          const cu = clientsMap.get(data.recipientId);
          if (cu) {
            setUserToInvite(cu);
          }
        },
      }
    : {
        name: 'Copy eSign link',
        icon: <CopyIcon />,
        onClick: handleCopyShareLink,
      };

  const cancelContractAction = {
    name: 'Cancel request',
    icon: <TrashIcon />,
    isDelete: true,
    onClick: () => cancelContract({ id: data.id }),
  };

  const downloadContractAction = {
    name: 'Download',
    icon: <Download />,
    onClick: handleDownload,
  };

  /**
   *  Determines the available actions based on the status of the contract.
   * - Pending contracts: options include copy link, download, and cancel request.
   * - Signed contracts: only download option is available.
   */
  const getActionsBasedOnStatus = (): Action[] => {
    switch (data.status) {
      case ContractStatus.Pending:
        return [
          copyContractAction,
          downloadContractAction,
          cancelContractAction,
        ];
      case ContractStatus.Signed:
        return [downloadContractAction];
      default:
        return ensureUnreachable(data.status);
    }
  };

  return (
    <>
      <BaseActionsMenu actions={getActionsBasedOnStatus()} />
      {userToInvite && (
        <CreateClientMagicLinkDialog
          clients={[userToInvite]}
          entityType="CONTRACT"
          nextPath={`/contracts/submit?contractId=${data.ref}`}
          onCancel={() => {
            setUserToInvite(undefined);
          }}
          onSuccess={() => {
            setUserToInvite(undefined);
          }}
          open={true}
          shareTypes={{ email: false, link: true }}
        />
      )}
    </>
  );
};

const selector = createSelector(
  [
    (state: RootState) => state.clients.clients,
    (state: RootState) => state.clients.companies,
    (state: RootState) => state.ui.searchValue,
  ],
  (clients, companies, searchKey) => ({
    clients: arrayToMap(clients, 'id'),
    companies: arrayToMap(companies, 'id'),
    searchKey,
  }),
);

export const SubmissionAndRequestTable = ({
  selectedTemplate,
  onContractRowClick,
}: {
  selectedTemplate: ContractTemplate | undefined;
  onContractRowClick?: (contract: Contract) => void;
}) => {
  const { query } = useContext(RouteContext);
  const { templateId } = query as ContractDetailsPageQueryParams;
  const portalConfig = React.useContext(PortalConfigContext);
  const [gridApi, setGridApi] = React.useState<GridApi>();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { clients, companies, searchKey } = useAppSelector(selector);

  const { data: submissionsAndRequests, isLoading } = useGetContractsQuery(
    {
      contractTemplateId: templateId,
    },
    {
      skip: !templateId, // we only want to fetch data when templateId is defined
    },
  );
  const { classes } = useStyles();
  const Icon = useModuleIcon('contracts');

  const columns: ColDef[] = useMemo(() => {
    // Generates columns to display submitted data from the client based on the selected contract template fields
    /* @ts-ignore: Types of property 'headerName' are incompatible. Type 'Element' is not assignable to type 'string'.ts(2322) */
    const clientSubmissionCols: ColDef[] = (selectedTemplate?.fields || [])
      .filter(
        (f) =>
          (f.inputType === InputType.Client && f.type === FieldType.Text) ||
          f.inputType === InputType.Variable,
      )
      .map((field) => ({
        // The ts-ignore is only here for this field. AG Grid only wants a string
        // for headerName, but it does render the Element we pass. Rather than
        // remove the feature of rendering an icon in the header col, let's just
        // add this one special case where we ignore ts.
        headerName: (
          <div className={classes.columnHeader}>
            {getSignIconBasedOnFieldType(field.type)} <div>{field.label}</div>
          </div>
        ),
        field: field.id,
        sortable: true,
        minWidth: 252,
      }));

    return [
      {
        headerName: 'Recipient',
        field: 'recipient',
        minWidth: 250,
        getQuickFilterText(filterParams) {
          const { client, company } = filterParams.data.recipientData;
          return `${client?.givenName} ${client?.familyName} ${client?.email} ${company?.name}`;
        },
        cellRenderer: 'contractClientRenderer',
      },
      {
        headerName: 'Sent on',
        field: 'shareDate',
        minWidth: 200,
        cellRenderer: 'textCellRenderer',
        getQuickFilterText(filterParams) {
          return DateUtils.formatDate(filterParams.data.sentOn);
        },
        valueFormatter: ({ value }) =>
          value ? DateUtils.formatDate(value) : '',
        sortable: true,
        sort: 'desc',
      },
      {
        headerName: 'Submitted on',
        field: 'submissionDate',
        minWidth: 200,
        cellRenderer: 'textCellRenderer',
        getQuickFilterText(filterParams) {
          return DateUtils.formatDate(filterParams.data.sentOn);
        },
        valueFormatter: ({ value }) =>
          value ? DateUtils.formatDate(value) : '',
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 150,
        sortable: true,
        cellRenderer: 'statusCellRenderer',
        cellRendererParams: {
          getStatusVariant: (status: ContractStatus) => {
            return getContractStatusInfo(status).statusVariant;
          },
        },
      },
      ...(clientSubmissionCols || []),
      {
        colId: 'actions',
        headerName: 'Actions',
        pinned: 'right',
        suppressSizeToFit: true,
        cellRenderer: 'actionCellRenderer',
        cellRendererParams: {
          clientsMap: clients,
          enableMagicLinks: portalConfig.authPreferences.enableMagicLinks,
        },
      },
    ];
  }, [selectedTemplate]);

  const rowData = useMemo(() => {
    if (!submissionsAndRequests) return [];

    return submissionsAndRequests.map((item) => {
      // Extract client-submitted values from the fields array and convert them into an object
      // with the shape: { [fieldId]: fieldValue }
      const clientSubmittedValues = item?.fields
        ?.filter(
          (f) =>
            f.inputType === InputType.Client ||
            f.inputType === InputType.Variable,
        )
        .reduce(
          (acc, { id, value }) => {
            acc[id] = value;
            return acc;
          },
          {} as Record<string, string | undefined>,
        );

      const company = companies.get(item.companyId);
      const client = clients.get(item.clientId ?? item.recipientId);

      return {
        ...item,
        ...clientSubmittedValues,
        recipientData: {
          client: client?.fields,
          company: company?.fields,
        },
      };
    });
  }, [submissionsAndRequests, clients, companies]);

  const shouldShowStandardPage = !rowData.length;

  const griOptions = React.useMemo(() => ({ gridApi, setGridApi }), [gridApi]);

  return (
    <LoadingWrapper isLoading={isLoading} hideContentWhileLoading>
      {shouldShowStandardPage ? (
        <StandardPage
          topIcon={Icon}
          title="This contract has not been shared yet"
          desc="Contract submissions and requests will show here."
          actionButton={
            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsShareModalOpen(true)}
            >
              Send contract
            </Button>
          }
        />
      ) : (
        <div className={classes.root}>
          <AgGridContext.Provider value={griOptions}>
            <AgGrid
              searchKey={searchKey}
              className="agGridRowHover"
              columnDefs={columns}
              rowData={rowData || []}
              getRowNodeId={(row: Contract) => row.id}
              frameworkComponents={{
                textCellRenderer: TextCellRenderer,
                actionCellRenderer: SubmissionTableActionMenu,
                contractClientRenderer: ContractClientRenderer,
                statusCellRenderer: StatusCellRenderer,
              }}
              onRowClicked={(value: Contract) => {
                if (onContractRowClick) {
                  onContractRowClick(value);
                } else {
                  history.push(
                    `${CONTRACTS_PAGE.path}/submission?contractId=${value.id}`,
                  );
                }
              }}
            />
          </AgGridContext.Provider>
        </div>
      )}
      <ShareContractModal
        open={isShareModalOpen}
        selectedTemplate={selectedTemplate}
        onClose={() => setIsShareModalOpen(false)}
      />
    </LoadingWrapper>
  );
};
